import { Audience } from '@/models/audiences';
import { DataSource, EDataSourceStatus } from '@/models/dataSource';
import { useGetDataSourcesByIdsQuery } from '@/services/dataSources';
import { useGetAudiencesQuery } from '@/services/endpoints/audiences';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useMemo } from 'react';
import { DRAFT_NAME_PREFIX, FISCAL_CODE_TRAIT_ID } from './constants';
import { isUploadedList, isCitizenFormList } from './listKind';

export type ComuniList = {
  id: string;
  name: string;
  createdOn: string;
  audience: Audience;
  datasources: DataSource[];
  /** True for lists created by the import flow (1:1 audience named like its datasource). */
  isUpload: boolean;
  /** True for lists saved from the communication composer (combined and/or filtered). */
  isCombo: boolean;
  status: EDataSourceStatus;
};

// ponytail: single page with a high limit, add paging if a comune ever exceeds it
const PAGE_LIMIT = 500;

const statusOf = (datasources: DataSource[]): EDataSourceStatus => {
  if (datasources.some((ds) => ds.status === EDataSourceStatus.ERROR)) return EDataSourceStatus.ERROR;
  if (datasources.some((ds) => ds.status !== EDataSourceStatus.ACTIVE)) return EDataSourceStatus.PENDING;
  return EDataSourceStatus.ACTIVE;
};

/** Every Comuni list is an audience whose matching trait is the fiscal code, joined 1:1..n with its datasources. */
export const useComuniLists = () => {
  const { id: workspaceId } = useWorkspace();
  const audiences = useGetAudiencesQuery({ workspaceId, limit: PAGE_LIMIT });

  /**
   * Only the datasources the lists actually link to: listing every datasource costs ~36ms
   * each server-side (159 of them took 5.8s on dev), while /datasources/multi with the
   * handful of ids we need answers in ~0.3s.
   */
  const linkedIds = useMemo(
    () => [
      ...new Set(
        (audiences.data?.items ?? []).filter((audience) => audience.matchingTrait === FISCAL_CODE_TRAIT_ID).flatMap((audience) => audience.datasourceIds ?? [])
      ),
    ],
    [audiences.data]
  );

  const dataSourcesSkipped = !workspaceId || linkedIds.length === 0;
  const dataSources = useGetDataSourcesByIdsQuery({ workspaceId, datasourceIds: linkedIds }, { skip: dataSourcesSkipped });

  const lists = useMemo<ComuniList[]>(() => {
    const dataSourceById = new Map((dataSources.data ?? []).map((ds) => [ds.id, ds]));
    return (
      (audiences.data?.items ?? [])
        .filter((audience) => audience.matchingTrait === FISCAL_CODE_TRAIT_ID)
        // in-flight communication drafts are campaign plumbing, not lists
        .filter((audience) => !audience.name.startsWith(DRAFT_NAME_PREFIX))
        .map((audience) => {
          const linked = (audience.datasourceIds ?? []).map((id) => dataSourceById.get(id)).filter((ds): ds is DataSource => !!ds);
          const isUpload = isUploadedList(audience, linked);
          return {
            id: audience.id,
            name: audience.name,
            createdOn: audience.createdOn,
            audience,
            datasources: linked,
            isUpload,
            isCombo: !isUpload && !isCitizenFormList(audience, linked),
            status: statusOf(linked),
          };
        })
    );
  }, [audiences.data, dataSources.data]);

  return {
    lists,
    isLoading: audiences.isLoading || (!dataSourcesSkipped && dataSources.isLoading),
    isError: audiences.isError || (!dataSourcesSkipped && dataSources.isError),
    refetch: () => {
      audiences.refetch();
      // skipped while no list links a datasource: refetching an unstarted query only warns
      if (!dataSources.isUninitialized) dataSources.refetch();
    },
  };
};
